import React from 'react';
import { motion } from 'framer-motion';

const BNPLLandingPage = ({ goToInput, theme }) => {
  // Theme-based button styling
  const buttonStyles = {
    mint: "bg-emerald-500 hover:bg-emerald-600",
    pink: "bg-pink-500 hover:bg-pink-600",
    lavender: "bg-purple-500 hover:bg-purple-600"
  };

  return (
    <motion.div
      className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 mb-8"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* SEO-optimized heading structure */}
      <h1 className="text-3xl font-bold text-center mb-6 dark:text-white">
        Buy Now, Pay Later: The Hidden Costs of Instant Gratification
      </h1>
      
      {/* Hero section with main CTA */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-6 mb-8">
        <div className="flex flex-col md:flex-row items-center">
          <div className="mb-6 md:mb-0 md:mr-6 text-center md:text-left">
            <h2 className="text-2xl font-bold mb-3 dark:text-white">The True Cost of "Pay Later" Options</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              BNPL services make spending feel painless, but can lead to debt accumulation and financial strain. Track your shopping habits and understand the hidden psychological triggers.
            </p>
            <motion.button
              onClick={goToInput}
              className={`px-6 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Track Your BNPL Purchases
            </motion.button>
          </div>
          <div className="text-8xl">💸</div>
        </div>
      </div>
      
      {/* Major BNPL Players Section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Major Buy Now, Pay Later Services</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <div>
                <h3 className="font-bold dark:text-white">Affirm</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm mt-1">
                  Offers longer-term financing options with interest rates from 0-30% APR depending on creditworthiness.
                </p>
                <a href="https://www.affirm.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 text-sm mt-2 inline-block hover:underline">
                  Visit Affirm →
                </a>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <div>
                <h3 className="font-bold dark:text-white">Klarna</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm mt-1">
                  Offers "Pay in 4" installments with no interest but charges late fees. Also has financing options with interest.
                </p>
                <a href="https://www.klarna.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 text-sm mt-2 inline-block hover:underline">
                  Visit Klarna →
                </a>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <div>
                <h3 className="font-bold dark:text-white">Afterpay</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm mt-1">
                  Splits purchases into four equal payments due every two weeks with no interest, but charges late fees.
                </p>
                <a href="https://www.afterpay.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 text-sm mt-2 inline-block hover:underline">
                  Visit Afterpay →
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* BNPL vs Credit Cards */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">How BNPL Differs From Credit Cards</h2>
        <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="font-bold text-lg mb-2 dark:text-white">Credit Cards</h3>
              <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                <li>• Require credit approval and hard credit checks</li>
                <li>• Continuous line of credit with revolving balance</li>
                <li>• Can build credit history with responsible use</li>
                <li>• Often have rewards programs and buyer protections</li>
                <li>• Higher interest rates (typically 15-30% APR)</li>
                <li>• Regulated as financial products</li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-bold text-lg mb-2 dark:text-white">Buy Now, Pay Later</h3>
              <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                <li>• Often no hard credit check required</li>
                <li>• One-time loan for each purchase</li>
                <li>• Limited or no credit building benefits</li>
                <li>• Few rewards or buyer protections</li>
                <li>• "Interest-free" options with hidden costs</li>
                <li>• Less regulated than traditional credit</li>
              </ul>
            </div>
          </div>
        </div>
        <p className="text-gray-600 dark:text-gray-300">
          The easier approval process for BNPL services makes them accessible to those with limited or poor credit history, but this convenience comes with its own risks and costs.
        </p>
      </section>
      
      {/* Hidden Costs Section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">The Hidden Costs of BNPL Services</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Merchant Fees</h3>
            <p className="text-gray-600 dark:text-gray-300 text-sm mt-1">
              BNPL providers charge merchants 3-6% of each transaction (compared to 1.5-3.5% for credit cards). These costs can be passed to consumers through higher prices.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Late Fees</h3>
            <p className="text-gray-600 dark:text-gray-300 text-sm mt-1">
              While many BNPL services advertise "interest-free" payments, they can charge substantial late fees — often $5-$15 per missed payment, which can equate to high effective APRs.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Data Collection & Privacy</h3>
            <p className="text-gray-600 dark:text-gray-300 text-sm mt-1">
              BNPL companies collect extensive user data, which they may sell to third parties or use for targeted marketing, encouraging more spending.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Debt Selling</h3>
            <p className="text-gray-600 dark:text-gray-300 text-sm mt-1">
              Some BNPL providers sell their consumer loans to larger financial institutions, potentially leading to more aggressive collection practices if payments are missed.
            </p>
          </div>
        </div>
      </section>
      
      {/* Psychological Manipulation */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Psychological Manipulation Tactics</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="mr-2">🧠</span>
              Pain of Paying Reduction
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              BNPL reduces the "pain of paying" by breaking purchases into seemingly small amounts. This psychological effect makes spending feel less impactful, encouraging larger purchases.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="mr-2">⏰</span>
              Temporal Discounting
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Humans naturally value immediate rewards over future costs. BNPL exploits this by providing immediate gratification while pushing financial consequences into the future.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="mr-2">🚨</span>
              Artificial Urgency
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              BNPL options are often presented with urgency-inducing messages like "limited time offer" or "exclusive financing" to push immediate purchase decisions.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="mr-2">🎯</span>
              Strategic Design Choices
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              BNPL options are frequently pre-selected, highlighted, or positioned prominently in checkout flows. These design nudges influence consumer behavior and increase BNPL adoption.
            </p>
          </div>
        </div>
      </section>
      
      {/* Dangers for Financially Illiterate */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Dangers for the Financially Vulnerable</h2>
        <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-4 text-gray-700 dark:text-gray-300">
          <h3 className="font-bold mb-3 dark:text-white">Instant Gratification Trap</h3>
          <p className="mb-4">
            BNPL services specifically target consumers who struggle with delayed gratification or financial literacy. They make it easy to overspend without understanding the long-term consequences.
          </p>
          
          <h3 className="font-bold mb-3 dark:text-white">Loan Stacking</h3>
          <p className="mb-4">
            Using multiple BNPL services simultaneously ("loan stacking") can quickly lead to unmanageable debt. Since most BNPL providers don't share data with credit bureaus, they can't see your other BNPL commitments.
          </p>
          
          <p className="font-semibold">
            BNPL services are designed to feel like they're helping you afford things, but they ultimately encourage spending beyond your means—especially for those without strong financial discipline.
          </p>
        </div>
      </section>
      
      {/* Using BNPL Wisely */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">How to Use BNPL Responsibly (If At All)</h2>
        <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4">
          <h3 className="font-bold mb-3 dark:text-white">Advantageous Only When Used Strategically</h3>
          <div className="space-y-3 text-gray-700 dark:text-gray-300">
            <div className="flex items-start">
              <span className="text-green-600 dark:text-green-400 mr-2">✓</span>
              <p><strong>Use only for planned, necessary large purchases</strong> - Not everyday shopping</p>
            </div>
            <div className="flex items-start">
              <span className="text-green-600 dark:text-green-400 mr-2">✓</span>
              <p><strong>Never spend more than you currently have</strong> - Ensure the money is already in your account</p>
            </div>
            <div className="flex items-start">
              <span className="text-green-600 dark:text-green-400 mr-2">✓</span>
              <p><strong>Avoid loan stacking</strong> - Limit yourself to one BNPL arrangement at a time</p>
            </div>
            <div className="flex items-start">
              <span className="text-green-600 dark:text-green-400 mr-2">✓</span>
              <p><strong>Set up automatic payments</strong> - Avoid late fees that can quickly erode any benefits</p>
            </div>
            <div className="flex items-start">
              <span className="text-green-600 dark:text-green-400 mr-2">✓</span>
              <p><strong>Track all BNPL commitments</strong> - Use a dedicated tool to monitor what you owe and when</p>
            </div>
            <div className="flex items-start">
              <span className="text-green-600 dark:text-green-400 mr-2">✓</span>
              <p><strong>Carefully read the terms</strong> - Understand late fees, interest rates, and other potential charges</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Common Questions About BNPL</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Does using BNPL affect my credit score?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              It depends. Many BNPL services don't report on-time payments to credit bureaus, but most report missed payments. Some services like Affirm report to credit bureaus for longer-term loans, which can help build credit if paid on time.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Are BNPL services safer than credit cards?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Not necessarily. Credit cards offer stronger consumer protections for disputes, returns, and fraud. Many BNPL services have more limited buyer protection policies and may be less accommodating with returns or defective merchandise.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">What happens if I miss a BNPL payment?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Most BNPL services charge late fees (typically $5-$15 per missed payment), may block you from making future purchases, and may report missed payments to credit bureaus. Repeated missed payments can lead to debt collection actions.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Why do retailers offer BNPL if they pay high fees?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Retailers see significant benefits: studies show BNPL increases average order values by 30-50% and improves conversion rates by 20-30%. These increases more than offset the merchant fees for most businesses.
            </p>
          </div>
        </div>
      </section>
      
      {/* CTA section */}
      <section className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 rounded-xl p-6 text-center">
        <h2 className="text-2xl font-bold mb-3 dark:text-white">Ready to Take Control of Your BNPL Spending?</h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          Start tracking your BNPL purchases with Guilt Gauge to understand your spending patterns and make more mindful financial decisions.
        </p>
        <motion.button
          onClick={goToInput}
          className={`px-8 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Track Your BNPL Purchases Now
        </motion.button>
      </section>
      
      {/* Rich SEO footer content */}
      <footer className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <h2 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-200">About Buy Now, Pay Later</h2>
          <p className="mb-3">
            Buy Now, Pay Later (BNPL) services have exploded in popularity, with millions of consumers using platforms like Affirm, Klarna, and Afterpay for purchases ranging from clothing to electronics and even groceries. While these services offer convenience and flexibility, they also encourage spending beyond means and can lead to debt cycles.
          </p>
          <p className="mb-3">
            Understanding the psychological tactics and hidden costs of BNPL services is essential for making informed financial decisions. Guilt Gauge helps you track your purchase patterns, identify emotional triggers, and develop healthier spending habits.
          </p>
          <p>
            Whether you're an occasional BNPL user or find yourself regularly relying on these services, our free tool helps you gain insight into your spending behavior and take control of your financial future.
          </p>
        </div>
      </footer>
    </motion.div>
  );
};

export default BNPLLandingPage;
